import { useEffect, useMemo, useState } from 'react';
import Modal from '@/components/ui/Modal';
import * as adminService from '@/services/adminService';
import { resolveAssetUrl } from '@/utils/assets';

const discountTypes = [
  { value: 'percentage', label: 'Percentagem' },
  { value: 'fixed', label: 'Valor fixo' },
  { value: 'free_item', label: 'Oferta direta' },
];

const emptyForm = {
  partnerId: '',
  title: '',
  description: '',
  imageUrl: '',
  discountType: 'percentage',
  discountPercent: 10,
  discountValue: 0,
  coinCost: 100,
  stock: -1,
  startsAt: '',
  expiresAt: '',
  isActive: true,
};

const fieldClass = 'w-full rounded-lg border border-fg/10 bg-black/25 px-3 py-2 text-sm text-fg outline-none focus:border-accent';
const labelClass = 'text-xs font-bold uppercase tracking-[0.16em] text-fg/40';

const formatDateForInput = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 16);
};

const formatDate = (value) => {
  if (!value) return 'Sem limite';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Sem limite';
  return date.toLocaleDateString('pt-PT');
};

export default function AdminOffersPage() {
  const [offers, setOffers] = useState([]);
  const [partners, setPartners] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const partnerById = useMemo(() => new Map(partners.map((partner) => [partner.id, partner])), [partners]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [offersRes, partnersRes] = await Promise.all([
        adminService.offers.list({ limit: 100 }),
        adminService.partners.list({ limit: 100 }),
      ]);
      const nextPartners = partnersRes.data.data || [];
      setOffers(offersRes.data.data || []);
      setPartners(nextPartners);
      setForm((current) => ({ ...current, partnerId: current.partnerId || nextPartners[0]?.id || '' }));
      setError('');
    } catch {
      setError('Não foi possível carregar as ofertas.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const resetForm = () => {
    setEditingId(null);
    setForm({ ...emptyForm, partnerId: partners[0]?.id || '' });
    setImageFile(null);
    setImagePreview('');
    setError('');
    setNotice('');
  };

  const editOffer = (offer) => {
    setEditingId(offer.id);
    setForm({
      partnerId: offer.partnerId || '',
      title: offer.title || '',
      description: offer.description || '',
      imageUrl: offer.imageUrl || '',
      discountType: offer.discountType || 'percentage',
      discountPercent: offer.discountPercent ?? 0,
      discountValue: offer.discountValue ?? 0,
      coinCost: offer.coinCost ?? 100,
      stock: offer.stock ?? -1,
      startsAt: formatDateForInput(offer.startsAt),
      expiresAt: formatDateForInput(offer.expiresAt),
      isActive: Boolean(offer.isActive),
    });
    setImageFile(null);
    setImagePreview(resolveAssetUrl(offer.imageUrl));
    setError('');
    setNotice('');
    setModalOpen(true);
  };

  const payload = () => ({
    ...form,
    discountPercent: Number(form.discountPercent) || 0,
    discountValue: Number(form.discountValue) || 0,
    coinCost: Number(form.coinCost) || 0,
    stock: Number(form.stock),
    startsAt: form.startsAt ? new Date(form.startsAt).toISOString() : null,
    expiresAt: form.expiresAt ? new Date(form.expiresAt).toISOString() : null,
  });

  const saveOffer = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setNotice('');
    try {
      let savedOffer;
      if (editingId) {
        const res = await adminService.offers.update(editingId, payload());
        savedOffer = res.data.data;
        setNotice('Oferta atualizada com sucesso.');
      } else {
        const res = await adminService.offers.create(payload());
        savedOffer = res.data.data;
        setNotice('Oferta criada com sucesso.');
      }

      if (imageFile && savedOffer?.id) {
        await adminService.uploadOfferImage(savedOffer.id, imageFile);
      }

      await loadData();
      resetForm();
      setModalOpen(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Não foi possível guardar a oferta.');
    } finally {
      setSaving(false);
    }
  };

  const selectImage = (file) => {
    setImageFile(file || null);
    setImagePreview(file ? URL.createObjectURL(file) : resolveAssetUrl(form.imageUrl));
  };

  const toggleActive = async (offer) => {
    try {
      await adminService.offers.update(offer.id, { isActive: !offer.isActive });
      await loadData();
    } catch {
      setError('Não foi possível alterar o estado da oferta.');
    }
  };

  const removeOffer = async (offer) => {
    if (!window.confirm(`Remover a oferta "${offer.title}"?`)) return;
    try {
      await adminService.offers.remove(offer.id);
      await loadData();
    } catch {
      setError('Não foi possível remover a oferta.');
    }
  };

  return (
    <section className="min-h-full">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-accent/70">Economia local</p>
          <h1 className="mt-1 font-serif text-3xl font-black text-accent">Gestão de Ofertas</h1>
          <p className="mt-2 max-w-2xl text-sm text-fg/55">Cria ofertas resgatáveis por QR, define custo em moedas, stock e validade.</p>
        </div>
        <button type="button" onClick={() => { resetForm(); setModalOpen(true); }} className="rounded-lg border border-accent/40 bg-accent/10 px-4 py-2 text-sm font-bold text-accent hover:bg-accent/20">
          Nova oferta
        </button>
      </div>

      {(error || notice) && (
        <div className={`mt-5 rounded-lg border p-4 text-sm font-bold ${error ? 'border-energy/40 bg-energy/10 text-energy' : 'border-forest-light/40 bg-forest-light/10 text-forest-light'}`}>
          {error || notice}
        </div>
      )}

      <section className="mt-6 rounded-lg border border-fg/10 bg-fg/[0.04]">
        <div className="flex items-center justify-between border-b border-fg/10 p-4">
          <h2 className="font-serif text-xl font-black text-accent">Ofertas registadas</h2>
          <span className="rounded-full border border-fg/10 bg-black/20 px-3 py-1 text-xs font-bold text-fg/55">{loading ? 'A carregar...' : `${offers.length} oferta(s)`}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[980px] text-left text-sm">
            <thead className="text-xs uppercase tracking-[0.16em] text-fg/35">
              <tr>
                <th className="px-4 py-3">Oferta</th>
                <th className="px-4 py-3">Parceiro</th>
                <th className="px-4 py-3">Desconto</th>
                <th className="px-4 py-3">Custo</th>
                <th className="px-4 py-3">Stock</th>
                <th className="px-4 py-3">Validade</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {!loading && offers.length === 0 && <tr><td className="px-4 py-6 text-fg/45" colSpan="8">Nenhuma oferta registada.</td></tr>}
              {offers.map((offer) => (
                <tr key={offer.id}>
                  <td className="px-4 py-3"><div className="font-bold text-fg">{offer.title}</div><div className="text-xs text-fg/40">{offer.description || 'Sem descrição'}</div></td>
                  <td className="px-4 py-3 text-fg/60">{partnerById.get(offer.partnerId)?.name || '-'}</td>
                  <td className="px-4 py-3 text-fg/60">{discountTypes.find((item) => item.value === offer.discountType)?.label || offer.discountType} · {offer.discountPercent || offer.discountValue || 0}</td>
                  <td className="px-4 py-3 text-fg/60">{offer.coinCost} moedas</td>
                  <td className="px-4 py-3 text-fg/60">{offer.stock < 0 ? 'Ilimitado' : offer.stock}</td>
                  <td className="px-4 py-3 text-fg/60">{formatDate(offer.expiresAt)}</td>
                  <td className="px-4 py-3"><button type="button" onClick={() => toggleActive(offer)} className={`rounded-full border px-3 py-1 text-xs font-bold ${offer.isActive ? 'border-forest-light/30 bg-forest-light/10 text-forest-light' : 'border-fg/15 bg-fg/5 text-fg/45'}`}>{offer.isActive ? 'Ativa' : 'Inativa'}</button></td>
                  <td className="px-4 py-3 text-right">
                    <button type="button" onClick={() => editOffer(offer)} className="mr-2 rounded-lg border border-ocean-light/30 px-3 py-1.5 text-xs font-bold text-ocean-light hover:bg-ocean-light/10">Editar</button>
                    <button type="button" onClick={() => removeOffer(offer)} className="rounded-lg border border-energy/30 px-3 py-1.5 text-xs font-bold text-energy hover:bg-energy/10">Remover</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} className="max-w-5xl">
        <div className="max-h-[86vh] overflow-y-auto rounded-lg border border-accent/25 bg-background p-5 shadow-gold">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-accent/60">Ofertas</p>
              <h2 className="font-serif text-2xl font-black text-accent">{editingId ? 'Editar oferta' : 'Nova oferta'}</h2>
            </div>
            <button type="button" onClick={() => setModalOpen(false)} className="rounded-lg border border-fg/10 px-3 py-1.5 text-sm font-bold text-fg/60 hover:bg-fg/10">Fechar</button>
          </div>
          <form className="mt-5 grid gap-3" onSubmit={saveOffer}>
            <label className="grid gap-1"><span className={labelClass}>Parceiro</span><select className={fieldClass} value={form.partnerId} onChange={(e) => setForm({ ...form, partnerId: e.target.value })} required>{partners.map((partner) => <option key={partner.id} value={partner.id}>{partner.name}</option>)}</select></label>
            <label className="grid gap-1"><span className={labelClass}>Título</span><input className={fieldClass} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required /></label>
            <label className="grid gap-1"><span className={labelClass}>Descrição</span><textarea className={`${fieldClass} min-h-24 resize-y`} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></label>
            <div className="grid gap-3 rounded-lg border border-fg/10 bg-black/15 p-3 sm:grid-cols-[180px_1fr]">
              <div className="h-36 overflow-hidden rounded-lg border border-fg/10 bg-black/25">
                {imagePreview ? (
                  <img src={imagePreview} alt="Pré-visualização da oferta" className="h-full w-full object-cover" />
                ) : (
                  <div className="grid h-full place-items-center px-4 text-center text-sm text-fg/35">Imagem da oferta</div>
                )}
              </div>
              <div className="grid content-center gap-2">
                <span className={labelClass}>Imagem da oferta</span>
                <input
                  className="block w-full text-sm text-fg/70 file:mr-3 file:rounded-lg file:border-0 file:bg-accent file:px-3 file:py-2 file:font-bold file:text-fg"
                  type="file"
                  accept="image/*"
                  onChange={(e) => selectImage(e.target.files?.[0])}
                />
                <input className={fieldClass} value={form.imageUrl} onChange={(e) => { setForm({ ...form, imageUrl: e.target.value }); setImagePreview(resolveAssetUrl(e.target.value)); }} placeholder="Ou cola uma URL externa" />
                <p className="text-xs text-fg/40">Usa uma imagem real da promoção, produto ou experiência do parceiro.</p>
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <label className="grid gap-1"><span className={labelClass}>Tipo</span><select className={fieldClass} value={form.discountType} onChange={(e) => setForm({ ...form, discountType: e.target.value })}>{discountTypes.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}</select></label>
              <label className="grid gap-1"><span className={labelClass}>Percentagem</span><input className={fieldClass} type="number" min="0" value={form.discountPercent} onChange={(e) => setForm({ ...form, discountPercent: e.target.value })} /></label>
              <label className="grid gap-1"><span className={labelClass}>Valor</span><input className={fieldClass} type="number" min="0" step="0.01" value={form.discountValue} onChange={(e) => setForm({ ...form, discountValue: e.target.value })} /></label>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="grid gap-1"><span className={labelClass}>Custo em moedas</span><input className={fieldClass} type="number" min="0" value={form.coinCost} onChange={(e) => setForm({ ...form, coinCost: e.target.value })} /></label>
              <label className="grid gap-1"><span className={labelClass}>Stock</span><input className={fieldClass} type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} /></label>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="grid gap-1"><span className={labelClass}>Início</span><input className={fieldClass} type="datetime-local" value={form.startsAt} onChange={(e) => setForm({ ...form, startsAt: e.target.value })} /></label>
              <label className="grid gap-1"><span className={labelClass}>Fim</span><input className={fieldClass} type="datetime-local" value={form.expiresAt} onChange={(e) => setForm({ ...form, expiresAt: e.target.value })} /></label>
            </div>
            <label className="flex items-center gap-2 rounded-lg border border-fg/10 bg-black/15 p-3 text-sm font-bold text-fg/70"><input type="checkbox" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />Ativa</label>
            <button type="submit" disabled={saving} className="rounded-lg bg-accent px-4 py-3 font-serif font-black text-fg shadow-gold disabled:opacity-60">{saving ? 'A guardar...' : editingId ? 'Guardar alterações' : 'Criar oferta'}</button>
          </form>
        </div>
      </Modal>
    </section>
  );
}
